import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listKyc, getKyc, reviewKyc, type KycSummary } from "../api/kyc";

function statusBadgeClass(status: string): string {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    default:
      return "bg-yellow-100 text-yellow-700";
  }
}

export function KycPage() {
  const { data, isLoading } = useQuery({ queryKey: ["kyc"], queryFn: () => listKyc() });
  const [selected, setSelected] = useState<number | null>(null);

  if (isLoading) return <p className="text-gray-500">Loading submissions…</p>;
  const rows = data ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">KYC / Compliance</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Business</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((k: KycSummary) => (
                <tr
                  key={k.kyc_id}
                  className={`cursor-pointer ${selected === k.kyc_id ? "bg-brand/5" : "hover:bg-gray-50"}`}
                  onClick={() => setSelected(k.kyc_id)}
                >
                  <td className="px-4 py-2 font-medium">
                    {k.legal_first_name} {k.legal_last_name}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{k.business_name || k.business_type || "—"}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs font-medium capitalize ${statusBadgeClass(
                        k.status,
                      )}`}
                    >
                      {k.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-500">{k.created_at || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <p className="text-gray-400 text-sm p-4">No submissions to review.</p>}
        </div>
        <div>
          {selected !== null ? (
            <Detail kycId={selected} />
          ) : (
            <p className="text-gray-400 text-sm">Select a submission to review its details.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm">{value === null || value === undefined || value === "" ? "—" : value}</div>
    </div>
  );
}

function Detail({ kycId }: { kycId: number }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["kyc", kycId], queryFn: () => getKyc(kycId) });
  const [note, setNote] = useState("");

  const review = useMutation({
    mutationFn: (status: string) => reviewKyc(kycId, { status, reviewer_note: note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kyc"] });
      qc.invalidateQueries({ queryKey: ["kyc", kycId] });
    },
  });

  if (isLoading) return <p className="text-gray-500">Loading details…</p>;
  if (!data) return <p className="text-gray-400 text-sm">Submission not found.</p>;

  return (
    <div className="bg-white rounded-lg shadow p-5 space-y-4">
      <div>
        <div className="text-lg font-bold">
          {data.legal_first_name} {data.legal_last_name}
        </div>
        <div className="text-xs text-gray-500">User #{data.usr_id}</div>
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-3">
        <Field label="Business type" value={data.business_type} />
        <Field label="Business name" value={data.business_name} />
        <Field label="Date of birth" value={data.date_of_birth} />
        <Field label="SSN (last 4)" value={data.ssn_last4} />
        <Field label="Address" value={data.address_line1} />
        <Field label="City" value={data.city} />
        <Field label="State" value={data.state} />
        <Field label="ZIP" value={data.zip_code} />
        <Field label="Country" value={data.country} />
        <Field label="EIN" value={data.ein} />
        <Field label="Use case" value={data.use_case} />
        <Field label="Call volume estimate" value={data.call_volume_estimate} />
      </div>

      <div className="border-t border-gray-100 pt-3">
        <div className="text-sm font-semibold text-gray-700 mb-2">Documents</div>
        {data.documents.length === 0 ? (
          <p className="text-gray-400 text-sm">No documents uploaded.</p>
        ) : (
          <ul className="space-y-1">
            {data.documents.map((doc) => (
              <li
                key={doc.kyc_document_id}
                className="flex items-center justify-between border border-gray-100 rounded px-3 py-2 text-sm"
              >
                <div>
                  <div className="font-medium">{doc.original_filename}</div>
                  <div className="text-xs text-gray-500">
                    {doc.document_type} · {doc.mime_type} · {doc.file_size} bytes
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3 space-y-2">
        <label className="block text-xs text-gray-500 mb-1">Reviewer note</label>
        <textarea
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="flex gap-2">
          <button
            disabled={review.isPending}
            onClick={() => review.mutate("approved")}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            Approve
          </button>
          <button
            disabled={review.isPending}
            onClick={() => review.mutate("rejected")}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded py-2 text-sm disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}
